import React from 'react';
import { IconButton, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { Division, LineItemUnitEnum } from '../glossary/glossary.type';
import { useQuery, useQueryClient } from 'react-query';
import keys from 'common/react-query/data-keys';
import { getCompsDivisionAverages, updateDivisionComps } from './report.api';
import { handleError } from 'common/utils/util.errors';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef, type MRT_Cell, type MRT_Row } from 'material-react-table';
import { formatCurrency, formatPercentage } from 'common/utils/common.util';
import { Link } from 'react-router-dom';
import { ROUTES } from 'common/constants/urls';
import { Button } from 'components/index';
import IconTooltip from '../common/IconTooltip';
import { Info, Refresh } from '@mui/icons-material';
import { REPORT_COLORS, WIDTHS } from './constants';
import { TOOLTIPS, compareArrays, getCompInfoString } from './report.util';
import { CreateLineItemDTO, ReportGeneralInfo, ReportLineItemTypes, ReportStatusEnum } from './report.type';
import { toast } from 'react-toastify';
import CircularProgress from '@mui/material/CircularProgress';
import { Comp } from '../comp/comp.type';

interface CompsAveragesProps {
  reportId: string;
  reportGeneralInfo: ReportGeneralInfo;
  reportStatus: string;
  reportSelectedComps: Comp[];
  division: Division;
  saveLineItem: (values: CreateLineItemDTO, showNotification?: boolean) => Promise<void>;
  registerReturnComp?: (divisionId: string, addCompFunc: () => Promise<CreateLineItemDTO>) => void;
  hardCost?: number;
  isLocked?: boolean;
  containerRef?: React.Ref<HTMLDivElement>;
}

const CompsAverages: React.FC<CompsAveragesProps> = ({
  reportId,
  reportGeneralInfo,
  reportStatus,
  reportSelectedComps,
  division,
  saveLineItem,
  hardCost = 0,
  isLocked,
  registerReturnComp,
  containerRef,
}) => {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch: refetchAverages,
  } = useQuery([keys.compsAverages, reportId, division?.id], () => getCompsDivisionAverages(reportId, division.id), {
    onSuccess(data) {
      setIncludedCompsIds(data.filter(comp => comp.isIncluded).map(comp => comp.compId));
    },
  });
  const queryClient = useQueryClient();
  const [isAddingComp, setIsAddingComp] = React.useState(false);
  const [includedCompsIds, setIncludedCompsIds] = React.useState<string[]>([]);
  const [isSavingCompsSelection, setIsSavingCompsSelection] = React.useState(false);
  const isPercentageDivision = ['1.00', '17.00', '18.00'].includes(division.code);

  const cannotUpdateCompsSelection = React.useMemo(() => {
    return compareArrays(
      includedCompsIds,
      data?.filter(comp => comp.isIncluded).map(comp => comp.compId),
    );
  }, [includedCompsIds, data]);

  const handleIncludedCompsChange = React.useCallback((compId: string, value: boolean) => {
    if (value) {
      setIncludedCompsIds(prev => [...prev, compId]);
    } else {
      setIncludedCompsIds(prev => prev.filter(id => id !== compId));
    }
  }, []);

  if (isError) {
    handleError(error);
    return null;
  }

  const compsAverages = React.useMemo(() => {
    const comps = data ? [...data] : [];
    comps?.sort((a, b) => {
      const comp1 = reportSelectedComps.find(comp => comp.id === a.compId);
      const comp2 = reportSelectedComps.find(comp => comp.id === b.compId);
      return (comp1?.project.projectTitle || '').localeCompare(comp2?.project.projectTitle || '') || 0;
    });
    return comps;
  }, [data, reportSelectedComps]);

  const singleAverage = React.useMemo(() => {
    const selectedComps = compsAverages.filter(comp => includedCompsIds.includes(comp.compId));
    if (selectedComps.length == 0) return 0;
    const total = selectedComps.reduce((acc, curr) => acc + curr.budgetPerUnit, 0);
    return total / selectedComps.length;
  }, [includedCompsIds, compsAverages]);

  const onAddComp = React.useCallback(async () => {
    try {
      if (!cannotUpdateCompsSelection) {
        // First save the comps selection
        setIsSavingCompsSelection(true);
        await updateDivisionComps(reportId, {
          divisionId: division.id,
          newProjectDrawIds: includedCompsIds.map(compId => compId.toString()),
        });
        queryClient.invalidateQueries([keys.compsAverages, reportId, division.id]);
      }

      // Then add the comparable pricing
      const quantity = isPercentageDivision ? 1 : reportGeneralInfo.grossBuildingSF || 0;
      const cdccCompPerUnit = isPercentageDivision ? (singleAverage / 100) * hardCost : singleAverage;
      const unit = isPercentageDivision ? LineItemUnitEnum.LS : LineItemUnitEnum.SF;

      const lineItem: CreateLineItemDTO = {
        reportId: reportId,
        description: `Comparable Pricing - ${division.name}`,
        divisionId: division.id,
        totalBudget: 0,
        quantity: quantity,
        unit: unit,
        cdccCompPerUnit: cdccCompPerUnit,
        type: ReportLineItemTypes.CompPricing,
      };
      setIsAddingComp(true);
      await saveLineItem(lineItem, false);
      toast.success(`Comparable Pricing for Division '${division.code}-${division.name}' updated successfully!`);
    } catch (e) {
      handleError(e);
    } finally {
      setIsAddingComp(false);
      setIsSavingCompsSelection(false);
    }
  }, [
    reportId,
    division.id,
    singleAverage,
    isLocked,
    includedCompsIds,
    queryClient,
    reportGeneralInfo.grossBuildingSF,
    hardCost,
    isPercentageDivision,
    cannotUpdateCompsSelection,
  ]);

  const onReturnComp = React.useCallback(
    async (individualCall = false): Promise<CreateLineItemDTO> => {
      const quantity = isPercentageDivision ? 1 : reportGeneralInfo.grossBuildingSF || 0;
      const cdccCompPerUnit = isPercentageDivision ? (singleAverage / 100) * hardCost : singleAverage;
      const unit = isPercentageDivision ? LineItemUnitEnum.LS : LineItemUnitEnum.SF;

      const lineItem: CreateLineItemDTO = {
        reportId: reportId,
        description: `Comparable Pricing - ${division.name}`,
        divisionId: division.id,
        totalBudget: 0,
        quantity: quantity,
        unit: unit,
        cdccCompPerUnit: cdccCompPerUnit,
        type: ReportLineItemTypes.CompPricing,
      };
      return lineItem;
    },
    [reportId, reportGeneralInfo.grossBuildingSF, division.id, division.name, singleAverage, hardCost, isPercentageDivision],
  );

  // Register ReturnComp function
  React.useEffect(() => {
    if (division && registerReturnComp) {
      registerReturnComp(division.id, onReturnComp);
    }
  }, [division, onReturnComp, onAddComp, registerReturnComp, singleAverage]);

  const CustomFooter = () => {
    let averageString = '';
    if (isPercentageDivision) {
      averageString += `${formatPercentage(singleAverage.toString())} Average`;
      if (hardCost) {
        averageString += ` - ${formatCurrency(((hardCost * singleAverage) / 100).toString())}`;
      }
    } else {
      averageString += `${formatCurrency(singleAverage.toString())} Average`;
    }

    return (
      <Stack
        direction={'row'}
        px={2}
        py={1}
        spacing={1}
        justifyContent={'space-between'}
        alignItems={'center'}
        sx={{ backgroundColor: REPORT_COLORS.COMPS_TABLE_COLOR }}
      >
        <Stack direction={'row'} alignItems={'center'}>
          <Typography variant='h6'>{averageString}</Typography>
          <Tooltip
            title={
              isLocked && reportStatus === ReportStatusEnum.Completed
                ? TOOLTIPS.statusCompleted
                : isLocked
                  ? 'Unlock report to recalculate'
                  : 'Recalculate'
            }
            arrow={false}
          >
            <span>
              <IconButton onClick={() => refetchAverages()} color='primary' disabled={isLocked}>
                <Refresh fontSize='medium' />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Stack direction={'row'} spacing={1}>
          <Tooltip
            title={
              isLocked && reportStatus === ReportStatusEnum.Completed ? TOOLTIPS.statusCompleted : isLocked ? TOOLTIPS.unlockToEdit : ''
            }
            arrow={false}
          >
            <span>
              <Button
                size='small'
                loading={isAddingComp || isSavingCompsSelection}
                onClick={onAddComp}
                disabled={compsAverages.length == 0 || isLocked}
                title='Use the average as comparable pricing for the division'
              >
                Add Comparable Pricing
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    );
  };

  const columns: MRT_ColumnDef<any>[] = React.useMemo(
    () => [
      {
        accessorKey: 'isIncluded',
        header: 'Selected',
        size: 100,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }: { row: MRT_Row<any> }) => {
          return (
            <Switch
              checked={includedCompsIds.includes(row.original.compId)}
              onChange={event => handleIncludedCompsChange(row.original.compId, event.target.checked)}
              disabled={isLocked}
            />
          );
        },
      },
      {
        accessorKey: 'budgetPerUnit',
        header: 'Budget',
        size: 100,
        Cell: ({ cell }: { cell: MRT_Cell<any> }) => (
          <span>{isPercentageDivision ? formatPercentage(cell.getValue<string>()) : formatCurrency(cell.getValue<string>())}</span>
        ),
      },
      {
        accessorKey: 'unit',
        header: 'Unit',
        size: 80,
        Cell: (): JSX.Element => <span>{isPercentageDivision ? '%' : '/SF'}</span>,
      },
      {
        accessorKey: 'project',
        header: 'Project',
        Cell: ({ row }: { row: MRT_Row<any> }) => {
          const comp = reportSelectedComps.find(comp => comp.id === row.original.compId);
          if (!comp) return <CircularProgress size={20} />;
          const tooltipContent = getCompInfoString(comp.project);
          return (
            <Stack direction={'row'} alignItems={'center'} spacing={2} p={0} sx={{ overflow: 'hidden', width: '100%', minWidth: 0 }}>
              <IconTooltip icon={<Info />} placement='bottom' title={tooltipContent} />
              <Stack direction={'row'} sx={{ flex: 1, minWidth: 0 }}>
                <Tooltip title={comp.project.projectTitle} arrow={false}>
                  <Link to={`${ROUTES.COMPS}/${row.original.compId}`} target='_blank' style={{ width: '100%', minWidth: 0 }}>
                    <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {comp.project.projectTitle}
                    </span>
                  </Link>
                </Tooltip>
              </Stack>
            </Stack>
          );
        },
        size: 200,
        enableSorting: false,
      },
    ],
    [division, reportSelectedComps, includedCompsIds, isLocked, isPercentageDivision],
  );

  const table = useMaterialReactTable({
    columns,
    data: compsAverages,
    getRowId: (row: { compId: string }) => row.compId, // eslint-disable-line react/prop-types
    enableTopToolbar: false,
    enableBottomToolbar: true,
    enablePagination: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableColumnActions: false,
    enableSorting: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    layoutMode: 'grid',
    state: {
      isLoading: isLoading || isFetching,
    },
    muiTableContainerProps: {
      sx: {
        height: 'calc(500px - 60px)', // Subtract bottom toolbar height
        minHeight: 'calc(500px - 60px)',
        maxHeight: 'calc(500px - 60px)',
        padding: 0,
        margin: 0,
        borderRadius: 0,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
      },
    },
    muiTablePaperProps: {
      sx: {
        height: '500px',
        minHeight: '500px',
        maxHeight: '500px',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        padding: 0,
        margin: 0,
        boxShadow: 'none',
        border: 'none',
        position: 'relative',
        overflow: 'hidden',
      },
    },
    muiBottomToolbarProps: {
      sx: {
        padding: 0,
        height: '60px',
        minHeight: '60px',
        borderTop: 'none',
        backgroundColor: 'transparent',
        marginTop: 'auto',
        position: 'sticky',
        bottom: 0,
        zIndex: 10,
        flexShrink: 0,
        width: '100%',
        '& .MuiToolbar-root': {
          padding: 0,
          height: '60px',
          minHeight: '60px',
          backgroundColor: 'white',
          borderTop: `1px solid ${REPORT_COLORS.BORDER}`,
        },
      },
    },
    muiTableProps: {
      sx: {
        borderRadius: 0,
        margin: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '& .MuiTableHead-root .MuiTableRow-root': {
          height: '52px',
          minHeight: '52px',
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          height: '52px',
          minHeight: '52px',
          display: 'flex',
          alignItems: 'center',
          padding: '8px 16px',
        },
        '& .MuiTableCell-root': {
          minHeight: '52px',
          padding: '8px 16px',
        },
        '& .MuiTableBody-root': {
          flex: 1,
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      // eslint-disable-next-line react/prop-types
      const backgroundColor = row.index % 2 === 0 ? '#ffffff' : '#f9f9f9';
      return {
        sx: {
          minHeight: '52px',
          height: 'auto',
          backgroundColor: backgroundColor + ' !important',
        },
      };
    },
    muiTableHeadProps: {
      sx: {
        padding: 0,
        color: 'white',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: REPORT_COLORS.COMPS_TABLE_COLOR,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
        borderBottom: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: '0',
        backgroundColor: REPORT_COLORS.COMPS_TABLE_COLOR,
        height: '52px',
        minHeight: '52px',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        border: 'none',
        backgroundColor: 'transparent !important',
        minHeight: '52px',
        padding: '8px 16px',
      },
    },
    renderBottomToolbar: () => <CustomFooter />,
  });

  return (
    <Stack ref={containerRef as any} width={WIDTHS.COMPS_TABLE_WIDTH} height='100%' sx={{ display: 'flex', flexDirection: 'column' }}>
      <MaterialReactTable table={table} />
    </Stack>
  );
};

export default React.memo(CompsAverages);
